import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, ChevronDown, ChevronRight, Inbox, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../../foundation/altzero_base/base/components/ui/dialog';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import { supabase } from '../../../../foundation/altzero_base/base/utils/supabaseClient';
import { billEmailService } from '../../services/billEmailService';
import { DataFreshness } from '../DataFreshness';
import {
  ProviderIngestMonitor, PROVIDERS, SILENT_DAYS_BEFORE_ALERT, loadDailyIngest, silentProviders, type DailyIngestRow,
} from '../ProviderIngestMonitor';
import { runDurationLabel } from '../../lib/billRunLog';
import { monthLabel } from '../../lib/billMonths';
import { runsLink, type FlowStep } from '../../lib/dashboardMetrics';

// Moved verbatim from BillValidationDashboard section 5 (Jobs dashboard spec §7).

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const USAGE_OPEN_KEY = 'bv-dashboard-usage-open';

// POST /gcs/sync answers in counts only: the GCS log has no org, so no NMI list and no file paths.
type SyncResult = {
  filesFound: number;
  filesSkipped: number;
  filesProcessed: number;
  filesErrored: number;
  rawRowsInserted: number;
  dailyRowsAggregated: number;
  unmatchedNMICount: number;
  errorCount: number;
  /** Another sync was already running, so this one did nothing. */
  skipped: boolean;
};

const getAuthHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    'x-api-key': 'scopingai',
    Authorization: `Bearer ${session?.access_token ?? ''}`,
    'x-user-id': session?.user?.id ?? '',
  };
};

const readUsageOpen = (): boolean | null => {
  try {
    const v = localStorage.getItem(USAGE_OPEN_KEY);
    return v == null ? null : v === '1';
  } catch {
    return null;
  }
};

type UsageDataFlowSectionProps = {
  month: string;
  /** The month's usageFlow, or null until the bills have loaded. */
  flow: FlowStep[] | null;
  blockedOnUsage: number;
  parseFailed: number;
  lastRun: { started_at: string; finished_at: string | null; status: string } | null;
  /** Providers currently silent, so the GCS card can turn amber. */
  onSilent?: (silent: string[]) => void;
};

export const UsageDataFlowSection = ({ month, flow, blockedOnUsage, parseFailed, lastRun, onSilent }: UsageDataFlowSectionProps) => {
  const link = (patch: Parameters<typeof runsLink>[1] = {}) => runsLink(month, patch);

  // ── Usage data flow: meter files in, usage onto bills ─────────────────────
  const [ingest, setIngest] = useState<DailyIngestRow[]>([]);
  const [ingestLoading, setIngestLoading] = useState(true);
  const [ingestError, setIngestError] = useState<string | null>(null);
  const [lastMeterSync, setLastMeterSync] = useState<string | null>(null);
  const [unmatched, setUnmatched] = useState<number | null>(null);

  const loadIngest = useCallback(async () => {
    setIngestLoading(true);
    try {
      setIngest(await loadDailyIngest());
      setIngestError(null);
    } catch (err) {
      setIngestError(err instanceof Error ? err.message : 'Failed to load ingest history');
    } finally {
      setIngestLoading(false);
    }
  }, []);

  const loadLastMeterSync = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/bill-validation/gcs/sync-log`, {
        headers: await getAuthHeaders(),
      });
      const json = await res.json();
      if (!json.success) return;
      const newest = (json.data as { processed_at: string; status: string }[] | undefined)
        ?.find((row) => row.status === 'success');
      setLastMeterSync(newest?.processed_at ?? null);
    } catch {
      // Freshness is a nice-to-have caption — a failed lookup shouldn't surface
      // as a dashboard error on top of whatever else is already shown.
      setLastMeterSync(null);
    }
  }, []);

  useEffect(() => {
    void loadIngest();
    void loadLastMeterSync();
    void billEmailService.getInboxCounts().then((r) => { if (r.success && r.data) setUnmatched(r.data.unmatched); });
  }, [loadIngest, loadLastMeterSync]);

  const { silent } = silentProviders(ingest, PROVIDERS, SILENT_DAYS_BEFORE_ALERT);

  // ── GCS sync (unchanged; moved into the usage section) ────────────────────
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  // GCS sync window (file UPLOAD date) — lives inside the Sync GCS dialog. It
  // only narrows which GCS files a sync pulls; it is not a dashboard filter.
  const [syncFrom, setSyncFrom] = useState('');
  const [syncTo, setSyncTo] = useState('');
  const [syncOpen, setSyncOpen] = useState(false);

  const handleGCSSync = useCallback(async () => {
    setSyncing(true);
    setSyncResult(null);
    setSyncError(null);
    try {
      const res = await fetch(`${API_BASE}/api/bill-validation/gcs/sync`, {
        method: 'POST',
        headers: {
          ...(await getAuthHeaders()),
          'Content-Type': 'application/json',
        },
        // Optional window on the GCS file upload date — empty = all files
        // (already-processed ones skip via the sync log either way).
        body: JSON.stringify(syncFrom || syncTo ? { from: syncFrom || undefined, to: syncTo || undefined } : {}),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Sync failed');
      setSyncResult(json.data);
      // Close on success only — a failure keeps the dialog open with the dates
      // still in it, so the user can adjust and retry rather than re-entering them.
      setSyncOpen(false);
      void loadLastMeterSync();
      void loadIngest();
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  }, [syncFrom, syncTo, loadLastMeterSync, loadIngest]);

  // Open by default when something is wrong with the meter feed, so a problem
  // never sits behind the fold; otherwise as the viewer last left it.
  const [usageOpen, setUsageOpen] = useState<boolean | null>(readUsageOpen);
  const usageProblem = silent.length > 0 || !!ingestError || (syncResult?.errorCount ?? 0) > 0;
  const usageShown = usageProblem || (usageOpen ?? false);
  const toggleUsage = () => {
    const next = !usageShown;
    setUsageOpen(next);
    try { localStorage.setItem(USAGE_OPEN_KEY, next ? '1' : '0'); } catch { /* per-viewer convenience only */ }
  };

  useEffect(() => { onSilent?.(silent); }, [onSilent, silent.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Card data-testid="dashboard-usage" className="border-0 shadow-sm">
        <CardContent className="pt-4 pb-4 space-y-4">
          <button type="button" onClick={toggleUsage} aria-expanded={usageShown}
            className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 text-left">
            <span className="flex items-center gap-1 text-sm font-semibold text-gray-800">
              {usageShown ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              Usage data flow
            </span>
            <DataFreshness timestamp={lastMeterSync} source="Meter data (GCS)" emptyLabel="never synced" />
            {PROVIDERS.map((p) => (
              <span key={p} className={cn('text-xs', silent.includes(p) ? 'text-amber-700 font-medium' : 'text-gray-500')}>
                {silent.includes(p) ? <AlertTriangle className="mr-1 inline h-3 w-3" /> : '✓ '}
                {p}{silent.includes(p) && ` silent ${SILENT_DAYS_BEFORE_ALERT}+ days`}
              </span>
            ))}
            {flow && blockedOnUsage > 0 && (
              <span className="text-xs text-amber-700">{blockedOnUsage} bill{blockedOnUsage === 1 ? '' : 's'} waiting on usage data</span>
            )}
          </button>

          {usageShown && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setSyncOpen(true)} disabled={syncing} className="gap-2"
                  title="Pull meter data files from GCS into the database. Choose an optional file-upload date range in the dialog.">
                  <RefreshCw className={cn('h-4 w-4', syncing && 'animate-spin')} />
                  {syncing ? 'Syncing...' : 'Sync GCS'}
                </Button>
                {lastRun && (
                  <span className="text-xs text-gray-500">
                    Last bill run {new Date(lastRun.started_at).toLocaleString('en-AU')} — {lastRun.status}
                    {lastRun.finished_at && ` in ${runDurationLabel(lastRun.started_at, lastRun.finished_at)}`}
                  </span>
                )}
                {unmatched != null && unmatched > 0 && (
                  <Link to="/bill-validation/inbox?status=unmatched" className="inline-flex items-center gap-1 text-xs text-amber-800 hover:underline">
                    <Inbox className="h-3.5 w-3.5" /> {unmatched} unmatched invoice{unmatched === 1 ? '' : 's'} in the Inbox
                  </Link>
                )}
                {parseFailed > 0 && (
                  <Link to={link({ conditions: { details: ['parse_failed'] } })} className="text-xs text-red-700 hover:underline">
                    {parseFailed} failed parse{parseFailed === 1 ? '' : 's'}
                  </Link>
                )}
              </div>

              {/* GCS sync result */}
              {syncResult?.skipped && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  Another GCS sync is already running — this one did nothing. Try again when it finishes.
                </div>
              )}
              {syncResult && !syncResult.skipped && (
                <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm">
                  <p className="font-medium text-green-800">
                    Sync complete — {syncResult.filesProcessed} file{syncResult.filesProcessed !== 1 ? 's' : ''} processed,{' '}
                    {syncResult.rawRowsInserted} interval rows upserted, {syncResult.dailyRowsAggregated} daily rows aggregated
                    {syncResult.filesSkipped > 0 && `, ${syncResult.filesSkipped} skipped (already processed)`}
                  </p>
                  {syncResult.unmatchedNMICount > 0 && (
                    <p className="mt-1 text-amber-700">
                      {syncResult.unmatchedNMICount} NMI{syncResult.unmatchedNMICount !== 1 ? 's' : ''} not registered in system
                    </p>
                  )}
                  {syncResult.errorCount > 0 && (
                    <p className="mt-1 text-red-700">
                      {syncResult.errorCount} file error{syncResult.errorCount !== 1 ? 's' : ''} — check server logs
                    </p>
                  )}
                </div>
              )}

              {/* Usage → bill: each step counts the bills that passed it; the drop is where they wait. */}
              {flow && (
                <div data-testid="dashboard-usage-flow">
                  <p className="mb-2 text-xs font-medium text-gray-600">{monthLabel(month)}: from invoice to validated</p>
                  <div className="flex flex-wrap items-stretch gap-1">
                    {flow.map((step, i) => {
                      const drop = i > 0 ? flow[i - 1].count - step.count : 0;
                      return (
                        <div key={step.key} className="flex items-center gap-1">
                          {i > 0 && <ArrowRight className="h-3.5 w-3.5 text-gray-300" />}
                          <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 min-w-[8.5rem]">
                            <p className="text-[11px] text-gray-500">{step.label}</p>
                            <p className="text-lg font-semibold text-gray-900">{step.count}</p>
                            {drop > 0 && <p className="text-[11px] text-amber-700">{drop} waiting here</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-3 text-xs">
                    <Link to={link({ conditions: { details: ['missing_usage_data'] } })} className="text-blue-700 hover:underline">Bills missing usage data</Link>
                    <Link to={link({ conditions: { details: ['missing_required_data'] } })} className="text-blue-700 hover:underline">Bills missing network / TOU data</Link>
                    <Link to="/bill-validation/report-hub/tariff-usage" className="text-blue-700 hover:underline">Interval data gaps by NMI</Link>
                  </div>
                </div>
              )}

              {/* Per-provider delivery. The DataFreshness caption reports the newest
                  sync of ANY file, so one provider going quiet stays invisible
                  behind the other's volume; this table shows each one. */}
              <ProviderIngestMonitor rows={ingest} loading={ingestLoading} error={ingestError} className="border border-gray-100 shadow-none" />
            </div>
          )}
        </CardContent>
      </Card>


      {/* Sync GCS dialog */}
      <Dialog open={syncOpen} onOpenChange={(open) => { if (!syncing) setSyncOpen(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Sync meter data from GCS</DialogTitle>
            <DialogDescription>
              Pulls NEM12 files from cloud storage into the database. The dates below are
              optional and filter by the date a file was <strong>uploaded to GCS</strong> — not
              by billing period. Files already processed are skipped either way.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[11px] uppercase tracking-wider text-gray-400">Uploaded from</span>
              <input
                type="date"
                value={syncFrom}
                max={syncTo || undefined}
                onChange={(e) => setSyncFrom(e.target.value)}
                className="h-9 rounded-md border border-gray-200 px-2 text-xs text-gray-700"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[11px] uppercase tracking-wider text-gray-400">Uploaded to</span>
              <input
                type="date"
                value={syncTo}
                min={syncFrom || undefined}
                onChange={(e) => setSyncTo(e.target.value)}
                className="h-9 rounded-md border border-gray-200 px-2 text-xs text-gray-700"
              />
            </label>
            {(syncFrom || syncTo) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-9 text-xs text-gray-500"
                onClick={() => { setSyncFrom(''); setSyncTo(''); }}
              >
                Clear
              </Button>
            )}
          </div>

          {syncError && (
            <p className="text-xs text-red-600">Sync failed: {syncError}</p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSyncOpen(false)} disabled={syncing}>
              Cancel
            </Button>
            <Button onClick={handleGCSSync} disabled={syncing} className="gap-2">
              <RefreshCw className={cn('h-4 w-4', syncing && 'animate-spin')} />
              {syncing ? 'Syncing...' : (syncFrom || syncTo) ? 'Sync selected range' : 'Sync all files'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
